import { useMutation, useQueryClient } from "@tanstack/react-query";
import { XCircle } from "lucide-react";
import * as React from "react";

import {
  addExpiryContext,
  dismissExpiredContextCard,
  type CloseReasonCategory,
  type DealListItem,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { FeedCard } from "@/components/ui/feed-card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/components/ui/form-error";
import { formatAud } from "@/lib/currency";
import { CLOSE_REASON_LABELS } from "@/lib/deal-labels";

// Order shown in the "Reason for closure" dropdown; "other" requires a note.
const CLOSE_REASONS: CloseReasonCategory[] = [
  "lost_to_competitor",
  "project_cancelled",
  "no_progress",
  "other",
];

const INPUT_CLASS =
  "mt-1 block w-full rounded border border-border bg-background p-2 text-sm transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

/** DD/MM/YYYY (Australian). */
function formatAuDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/**
 * Post-expiry "DEAL EXPIRED — CLOSED-LOST" feed card for the BDM/PO (CHP-380).
 *
 * Surfaces for a deal that auto-expired (LIVE→CLOSED_LOST via the SLA sweep)
 * and has no close reason recorded yet (expiry_context_added_at === null). The
 * primary [Add context] action expands an inline form — a "Reason for closure"
 * dropdown (the four CloseReasonCategory values), optional additional details,
 * and Confirm/Cancel — which POSTs add-expiry-context. The secondary [X]
 * POSTs dismiss-expired-context-card (CHP-599) so the dismissal persists
 * across both partner dashboards; the card also permanently drops off once
 * context is added (the derivation filters on both timestamps).
 */
export function DealExpiredContextCard({
  deal,
  onContextAdded,
  onDismiss,
}: {
  deal: DealListItem;
  /** Called after a successful add-expiry-context (route invalidates the feed). */
  onContextAdded?: () => void;
  /** CHP-1044: notifies the parent feed of the dismiss so its counts (badge,
   * "See N more") stay in lock-step. The dismissal itself is persisted
   * server-side (CHP-599) — the local hide is just instant feedback while the
   * POST + feed invalidation land. */
  onDismiss?: () => void;
}) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();

  const [dismissed, setDismissed] = React.useState(false);
  const [formOpen, setFormOpen] = React.useState(false);
  const [reason, setReason] = React.useState<CloseReasonCategory>("lost_to_competitor");
  const [notes, setNotes] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const addContext = useMutation({
    mutationFn: () =>
      addExpiryContext(accessToken!, deal.id, {
        close_reason_category: reason,
        close_notes: notes.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      onContextAdded?.();
    },
    onError: () => setError("Couldn't save the context. Refresh and try again."),
  });

  // CHP-599: persist the [X] server-side so the dismissal holds on BOTH
  // partner dashboards and across reloads. The card hides locally first
  // (instant feedback); the invalidation then drops it from every feed
  // derivation. Best-effort — a failed POST just means the card returns on
  // the next fetch, which is the pre-CHP-599 behaviour.
  const dismissCard = useMutation({
    mutationFn: () => dismissExpiredContextCard(accessToken!, deal.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
  });

  if (dismissed) return null;

  const notesRequired = reason === "other";
  // Preserve the original null-token contract: a missing value hides its meta
  // span entirely (the lib formatter would render "—" instead).
  const value = deal.total_contract_value != null ? formatAud(deal.total_contract_value) : null;

  return (
    // CHP-407: deliberately NOT a whole-card button (no onClick). There's
    // nothing to open until the close reason is recorded — the primary [Add
    // context] action expands an inline form in place, and that form must own
    // its own clicks. Mirrors the manager-side closed-lost "Add context" card.
    // CHP-523: coral lane (deal expired = urgent), built on the shared FeedCard.
    <FeedCard
      tone="coral"
      icon={XCircle}
      eyebrow="DEAL EXPIRED — CLOSED-LOST"
      rawTestId={`expired-context-card-${deal.id}`}
      onDismiss={() => {
        setDismissed(true);
        dismissCard.mutate();
        onDismiss?.();
      }}
      dismissLabel={`Dismiss expiry notification for ${deal.client_name}`}
      dismissTestId={`expired-context-dismiss-${deal.id}`}
      title={deal.client_name}
      description={
        <>
          {/* CHP-1006/1007: standard meta row (id · value · date) matching
              ExpiryNotificationCard, replacing the id+value-in-title divergence. */}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span>{deal.deal_id_display ?? "—"}</span>
            {value ? <span>{value}</span> : null}
            <span>Expired {formatAuDate(deal.closed_at ?? null)}</span>
          </div>

          {formOpen ? (
            <div className="mt-3 space-y-3 rounded border border-border p-3">
              <div>
                <Label htmlFor={`reason-${deal.id}`}>Reason for closure *</Label>
                <select
                  id={`reason-${deal.id}`}
                  value={reason}
                  onChange={(e) => setReason(e.target.value as CloseReasonCategory)}
                  className={INPUT_CLASS}
                  disabled={addContext.isPending}
                >
                  {CLOSE_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {CLOSE_REASON_LABELS[r]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor={`notes-${deal.id}`}>
                  Additional details{notesRequired ? " *" : " (Optional)"}
                </Label>
                <Textarea
                  id={`notes-${deal.id}`}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  className="mt-1"
                  disabled={addContext.isPending}
                />
              </div>
              {error ? <FormError>{error}</FormError> : null}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={notesRequired && notes.trim().length === 0}
                  loading={addContext.isPending}
                  loadingLabel="Updating…"
                  data-testid={`expired-context-confirm-${deal.id}`}
                  onClick={() => {
                    setError(null);
                    addContext.mutate();
                  }}
                >
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={addContext.isPending}
                  onClick={() => setFormOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </>
      }
      // CHP-1006/1007: the primary "Add context" action moves to the standard
      // right-aligned FeedCard action slot; while the inline form is open it's
      // hidden (the form owns Confirm/Cancel).
      action={
        !formOpen ? (
          <Button
            size="sm"
            data-testid={`expired-context-add-${deal.id}`}
            onClick={() => {
              setError(null);
              setReason("lost_to_competitor");
              setNotes("");
              setFormOpen(true);
            }}
          >
            Add context
          </Button>
        ) : undefined
      }
    />
  );
}
